
import React, { useEffect, useState } from 'react';
import ImageUpload from '../../../sharedcomponents/imageupload/imageupload';
import { fetchApi } from '../../../services/api';
import { buildD4Payload } from '../../../services/payloads/d4Payload';
import { getCurrentUserEmail } from '../../../services/auth';
import '../d1/d1.css';
import '../d2/d2.css';
import '../d4/d4.css';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Grid,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import TwoMonthDatePicker from '../../../sharedcomponents/two-month-datepicker/TwoMonthDatePicker';

const toIsoOrNull = (date) => {
  if (!date) return null;
  try {
    const d = date instanceof Date ? date : new Date(date);
    return isNaN(d.getTime()) ? null : d.toISOString();
  } catch {
    return null;
  }
};

const D4 = ({ 
  expanded, 
  onSetClick, 
  handleCloseForm, 
  onToggleAccordion, 
  isEnabled = false, 
  onSaved, 
  eightDInfoId, 
  currentUserEmail = getCurrentUserEmail() || sessionStorage.getItem('userEmail') || '', 
  teamMembers = [],
  d4Data,
  d4ResponsibleData,
  mode,
  readOnly = false,
}) => {
  // When readOnly is true, disable all edit interactions
  const effectiveEnabled = isEnabled && !readOnly;
  const canView = isEnabled;
  const [responsiblePerson, setResponsiblePerson] = useState('');
  const [plannedDate, setPlannedDate] = useState(null);
  const [achievedDate, setAchievedDate] = useState(null);
  const [errors, setErrors] = useState({ person: '', planned: '', achieved: '' });

  const [occurrenceRows, setOccurrenceRows] = useState([{ srNo: '1 (RC)', why: '' }]);
  const [nonDetectionRows, setNonDetectionRows] = useState([{ srNo: '1', why: '' }]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [occurrenceToRemove, setOccurrenceToRemove] = useState([]);
  const [nonDetectionToRemove, setNonDetectionToRemove] = useState([]);
  const [attachmentsToRemove, setAttachmentsToRemove] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);

  // Save state & notices
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveNotice, setSaveNotice] = useState(null);
  const [d4Id, setD4Id] = useState(null);
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [formSaved, setFormSaved] = useState(false);
  const [isResponsibleSet, setIsResponsibleSet] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); // Track if form has been interacted with
  const [showAchievedDate, setShowAchievedDate] = useState(false);
  const [formClosed, setFormClosed] = useState(true);

  // Determine if current user is the responsible person for this form
  const isCurrentUserResponsible = responsiblePerson && currentUserEmail && 
    responsiblePerson.toLowerCase() === currentUserEmail.toLowerCase();
  // Block form when user is not originator (readOnly) and not the responsible person
  const isFormBlocked = readOnly ? !isCurrentUserResponsible : false;

  // Check if responsible person is already set (only when d4Id exists, meaning Set was clicked)
  useEffect(() => {
    if (d4Id) {
      setIsResponsibleSet(true);
    }
  }, [d4Id]);

  // Load D4 data when available
  useEffect(() => {
    // Fetch from API if eightDInfoId exists and no props data
    if (eightDInfoId && !d4Data && !d4ResponsibleData && mode === 'edit') {
      const fetchD4Data = async () => {
        try {
          const response = await fetchApi('/api/D4/GetD4', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ EightDInfoId: Number(eightDInfoId) }),
          });
          if (response?.data) {
            const d4 = response.data;
            if (d4.D4Id || d4.d4Id) { setD4Id(d4.D4Id || d4.d4Id); setFormSaved(true); }
            if (d4.ResponsiblePerson) setResponsiblePerson(d4.ResponsiblePerson);
            if (d4.PlannedDate) setPlannedDate(new Date(d4.PlannedDate));
            if (d4.AchievedDate) setAchievedDate(new Date(d4.AchievedDate));
            if (d4.OccurrenceResult && Array.isArray(d4.OccurrenceResult) && d4.OccurrenceResult.length > 0) {
              const sortedOcc = [...d4.OccurrenceResult].sort((a, b) => (a.Srno || a.D4OccurrenceId || 0) - (b.Srno || b.D4OccurrenceId || 0));
              setOccurrenceRows(sortedOcc.map((occ, idx) => ({
                id: occ.D4OccurrenceId || occ.d4OccurrenceId || occ.Id || occ.id || null,
                srNo: idx === sortedOcc.length - 1 ? `${idx + 1} (RC)` : `${idx + 1}`,
                why: occ.Why || ''
              })));
            }
            if (d4.NonDetectionDetails && Array.isArray(d4.NonDetectionDetails) && d4.NonDetectionDetails.length > 0) {
              const sortedNd = [...d4.NonDetectionDetails].sort((a, b) => (a.Srno || a.D4NonDetectionId || 0) - (b.Srno || b.D4NonDetectionId || 0));
              setNonDetectionRows(sortedNd.map((nd, idx) => ({
                id: nd.D4NonDetectionId || nd.d4NonDetectionId || nd.Id || nd.id || null,
                srNo: `${idx + 1}`,
                why: nd.Why || ''
              })));
            }
            if (d4.Attachments && Array.isArray(d4.Attachments)) {
              setUploadedFiles(d4.Attachments);
            }
          }
        } catch (error) {
        }
      };
      fetchD4Data();
      return;
    }

    if (d4Data) {
      const data = Array.isArray(d4Data) ? d4Data[0] : d4Data;
      if (data) {
        if (data.D4Id || data.d4Id) { setD4Id(data.D4Id || data.d4Id); setFormSaved(true); }
        if (data.OccurrenceResult && Array.isArray(data.OccurrenceResult) && data.OccurrenceResult.length > 0) {
          const sortedOcc = [...data.OccurrenceResult].sort((a, b) => (a.Srno || a.D4OccurrenceId || 0) - (b.Srno || b.D4OccurrenceId || 0));
          setOccurrenceRows(sortedOcc.map((occ, idx) => ({
            id: occ.D4OccurrenceId || occ.d4OccurrenceId || occ.Id || occ.id || null,
            srNo: idx === sortedOcc.length - 1 ? `${idx + 1} (RC)` : `${idx + 1}`,
            why: occ.Why || ''
          })));
        }
        
        if (data.NonDetectionDetails && Array.isArray(data.NonDetectionDetails) && data.NonDetectionDetails.length > 0) {
          const sortedNd = [...data.NonDetectionDetails].sort((a, b) => (a.Srno || a.D4NonDetectionId || 0) - (b.Srno || b.D4NonDetectionId || 0));
          setNonDetectionRows(sortedNd.map((nd, idx) => ({
            id: nd.D4NonDetectionId || nd.d4NonDetectionId || nd.Id || nd.id || null,
            srNo: `${idx + 1}`,
            why: nd.Why || ''
          })));
        }
        
        if (data.Attachments && Array.isArray(data.Attachments)) {
          setUploadedFiles(data.Attachments);
        }
      }
    }
    
    if (d4ResponsibleData) {
      const respData = Array.isArray(d4ResponsibleData) ? d4ResponsibleData[0] : d4ResponsibleData;
      if (respData) {
        setResponsiblePerson(respData.ResponsiblePerson || '');
        setPlannedDate(respData.PlannedDate ? new Date(respData.PlannedDate) : null);
        setAchievedDate(respData.AchievedDate ? new Date(respData.AchievedDate) : null);
      }
    }
  }, [eightDInfoId, d4Data, d4ResponsibleData, mode]);

  const validateField = (field, value) => {
    let error = '';
    if (field === 'person' && !value) error = 'Responsible person is required';
    if (field === 'planned' && !value) error = 'Planned date is required';
    if (field === 'achieved' && !value) error = 'Achieved date is required';
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handlePersonChange = (value) => {
    setResponsiblePerson(value);
    validateField('person', value);
  };

  const handlePlannedChange = (value) => {
    setPlannedDate(value);
    validateField('planned', value);
  };

  const handleAchievedChange = (value) => {
    setAchievedDate(value);
    validateField('achieved', value);
  };

  const handleWhyChange = (rows, setRows, index, value) => {
    const updatedRows = [...rows];
    updatedRows[index].why = value;
    setRows(updatedRows);
    setIsFormTouched(true);
  };

  const MAX_ROWS = 10;

  const handleAction = (rows, setRows, index, isOccurrence) => {
    let updatedRows;
    if (index === rows.length - 1) {
      if (rows.length >= MAX_ROWS) return;
      const nextSrNo = isOccurrence ? `${rows.length + 1} (RC)` : `${rows.length + 1}`;
      updatedRows = [...rows, { srNo: nextSrNo, why: '' }];
    } else {
      const removedRow = rows[index];
      if (removedRow.id) {
        if (isOccurrence) {
          setOccurrenceToRemove(prev => [...prev, removedRow.id]);
        } else {
          setNonDetectionToRemove(prev => [...prev, removedRow.id]);
        }
      }
      updatedRows = rows.filter((_, i) => i !== index);
    }
    updatedRows = updatedRows.map((row, i) => ({
      ...row,
      srNo: i === updatedRows.length - 1
        ? (isOccurrence ? `${i + 1} (RC)` : `${i + 1}`)
        : `${i + 1}`,
    }));
    setRows(updatedRows);
    setIsFormTouched(true);
  };

  const renderTable = (rows, setRows, isOccurrence) => (
    <Table className="custom-table">
      <TableHead>
        <TableRow>
          <TableCell>Sr no.</TableCell>
          <TableCell>Why's</TableCell>
          <TableCell></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, index) => (
          <TableRow key={index}>
            <TableCell data-label="Sr no.">{row.srNo}</TableCell>
            <TableCell data-label="Why's">
              <input
                className="why-input"
                value={row.why}
                onChange={(e) => handleWhyChange(rows, setRows, index, e.target.value)}
                onFocus={() => setIsFormTouched(true)}
                onClick={() => setIsFormTouched(true)}
              />
            </TableCell>
            <TableCell data-label="Action">
              <button
                className="icon-btn"
                onClick={() => handleAction(rows, setRows, index, isOccurrence)}
                disabled={index === rows.length - 1 && rows.length >= MAX_ROWS}
              >
                {index === rows.length - 1 ? '+' : '−'}
              </button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const handleUpdateResponsiblePerson = async () => {
    if (!responsiblePerson) return;
    
    setSaveError(null);
    setSaveNotice(null);

    if (!eightDInfoId) {
      setSaveError('eightDInfoId is missing.');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        D4Id: d4Id || undefined,
        EightDInfoId: eightDInfoId,
        ResponsiblePerson: responsiblePerson,
        PlannedDate: plannedDate?.toISOString(),
        AchievedDate: achievedDate?.toISOString()
      };

      const endpoint = d4Id ? '/api/D4/UpdateD4Responsible' : '/api/D4/CreateD4';
      const response = await fetchApi(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response?.data?.D4Id || response?.data?.d4Id) {
        setD4Id(response.data.D4Id || response.data.d4Id);
      }

      setIsResponsibleSet(true);
      setFormClosed(false);
      setSaveNotice(d4Id ? "Updated D4 form successfully." : "D4 responsible person set successfully.");
      setTimeout(() => setSaveNotice(null), 3000);
    } catch (e) {
      setSaveError(e?.message ?? 'Failed to set responsible person.');
      setTimeout(() => setSaveError(null), 2000);
    } finally {
      setSaving(false);
    }
  };

  const toggleAccordion = () => {
    if (readOnly && isEnabled) {
      onToggleAccordion();
      return;
    }
    if (isEnabled && isEditMode && (d4Id || (responsiblePerson && plannedDate))) {
      onToggleAccordion();
    }
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaveNotice(null);
    
    if (!eightDInfoId) {
      setSaveError('EightDInfoId is missing.');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }
    
    // Validate newly added rows in Occurrence section
    const hasEmptyOccurrenceRows = occurrenceRows.some((row, index) => {
      // Check if this is a newly added row (more than the initial row) and is empty
      return index > 0 && (!row.why || row.why.trim() === '');
    });
    
    // Validate newly added rows in Non Detection section
    const hasEmptyNonDetectionRows = nonDetectionRows.some((row, index) => {
      // Check if this is a newly added row (more than the initial row) and is empty
      return index > 0 && (!row.why || row.why.trim() === '');
    });
    
    // Show alert if any newly added rows are empty
    if (hasEmptyOccurrenceRows || hasEmptyNonDetectionRows) {
      setSaveError('Please fill all mandatory fields.');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }
    
    setSaving(true);
    
    try {
      const payload = buildD4Payload({
        responsiblePerson,
        plannedDate,
        achievedDate,
        occurrenceRows,
        nonDetectionRows,
        uploadedFiles,
        isDisabled,
        occurrenceToRemove,
        nonDetectionToRemove,
        attachmentsToRemove,
        currentUserEmail,
        d4Id,
        eightDInfoId,
      });
      
      
      const endpoint = d4Id ? '/api/D4/UpdateD4' : '/api/D4/CreateD4';
      const data = await fetchApi(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      // Update d4Id from response if it's a create operation
      if (data?.data?.D4Id || data?.data?.d4Id) {
        setD4Id(data.data.D4Id || data.data.d4Id);
      }

      // Re-fetch D4 data to get assigned IDs for child records
      try {
        const refreshed = await fetchApi('/api/D4/GetD4', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ EightDInfoId: Number(eightDInfoId) }),
        });
        if (refreshed?.data) {
          const d4 = refreshed.data;
          if (d4.OccurrenceResult && Array.isArray(d4.OccurrenceResult) && d4.OccurrenceResult.length > 0) {
            const sortedOcc = [...d4.OccurrenceResult].sort((a, b) => (a.Srno || a.D4OccurrenceId || 0) - (b.Srno || b.D4OccurrenceId || 0));
            setOccurrenceRows(sortedOcc.map((occ, idx) => ({
              id: occ.D4OccurrenceId || occ.d4OccurrenceId || null,
              srNo: idx === sortedOcc.length - 1 ? `${idx + 1} (RC)` : `${idx + 1}`,
              why: occ.Why || ''
            })));
          }
          if (d4.NonDetectionDetails && Array.isArray(d4.NonDetectionDetails) && d4.NonDetectionDetails.length > 0) {
            const sortedNd = [...d4.NonDetectionDetails].sort((a, b) => (a.Srno || a.D4NonDetectionId || 0) - (b.Srno || b.D4NonDetectionId || 0));
            setNonDetectionRows(sortedNd.map((nd, idx) => ({
              id: nd.D4NonDetectionId || nd.d4NonDetectionId || null,
              srNo: `${idx + 1}`,
              why: nd.Why || ''
            })));
          }
          if (d4.Attachments && Array.isArray(d4.Attachments)) {
            setUploadedFiles(d4.Attachments);
          }
        }
      } catch (e) {
        // Non-critical: IDs will be fetched on next form open
      }

      setIsFormTouched(false);
      setOccurrenceToRemove([]);
      setNonDetectionToRemove([]);
      setAttachmentsToRemove([]);
      setSaveNotice('D4 root cause analysis saved successfully.');
      setTimeout(() => setSaveNotice(null), 2000);
      setFormSaved(true);
      
      // Notify parent that D4 is saved
      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      setSaveError(error?.message || 'Failed to save D4 root cause analysis.');
      setTimeout(() => setSaveError(null), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Box className={`d3-header ${canView ? 'enabled' : 'disabled'}`} onClick={toggleAccordion} style={{ cursor: canView ? 'pointer' : 'not-allowed', pointerEvents: canView ? 'auto' : 'none', opacity: canView ? 1 : 0.5 }}>
        <Box className="d3-left-section">
          <Box className="d3-circle" style={achievedDate && formSaved ? { backgroundColor: '#4ccb91', borderColor: '#4ccb91' } : (responsiblePerson || isEditMode || formSaved ? { backgroundColor: '#f8c239', borderColor: '#f8c239' } : {})} />
          <span className="d3-title">D4</span>
          <span className="d3-subtitle">Root Cause Analysis</span>

          <Tooltip
            title={
              <span>
                Identify the Possible Causes of the problem for both occurrence and non detection.
                Conduct a Root Cause Analysis using the 5 why's. Reproduce the defect to confirm
                the true root cause.
              </span>
            }
            arrow
            placement="bottom-start"
            componentsProps={{
              tooltip: { className: 'custom-tooltip' },
              arrow: { className: 'custom-arrow' }
            }}
          >
            <IconButton>
              <InfoOutlinedIcon sx={{ color: '#666' }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box className="responsible-box" onClick={(e) => e.stopPropagation()}>
          <Select
            value={responsiblePerson}
            onChange={(e) => handlePersonChange(e.target.value)}
            displayEmpty
            variant="standard"
            className="responsible-select"
            error={!!errors.person}
            disabled={!effectiveEnabled || isFormBlocked}
            sx={{
              '& .MuiInput-underline:after': {
                borderBottomColor: '#800040',
              },
              '& .MuiInput-underline:before': {
                borderBottomColor: '#ccc',
              },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                borderBottomColor: '#800040',
              }
            }}
          >
            <MenuItem value="" sx={{ display: 'none' }}>Responsible Person</MenuItem>
            {teamMembers.map((member) => (
              <MenuItem key={member.D1Id} value={member.EmailId || member.emailId}>
                {member.TeamMemberName || member.teamMemberName}
              </MenuItem>
            ))}
          </Select>
          {errors.person && (
            <Typography className="error-text">{errors.person}</Typography>
          )}
        </Box>

        <Box onClick={(e) => e.stopPropagation()}>
          <TwoMonthDatePicker
            value={plannedDate}
            onChange={(value) => handlePlannedChange(value)}
            label="Planned Date"
            disabled={!effectiveEnabled || isFormBlocked || (isResponsibleSet && !isEditMode)}
            InputLabelProps={{ style: { fontSize: '0.8rem', color: '#777' } }}
            sx={{ '& .MuiInputBase-input': { color: '#333' } }}
          />
        </Box>

        {/* Achieved Date - enabled when responsible person is set */}
        <Box onClick={(e) => {
          e.stopPropagation();
          if (isResponsibleSet && !readOnly && !expanded) {
            setIsEditMode(true);
            setFormClosed(false);
            setIsFormTouched(false);
            onToggleAccordion();
          }
        }}>
          <TwoMonthDatePicker
            value={achievedDate}
            onChange={(d) => {
              setAchievedDate(d);
              setIsFormTouched(true);
              if (!isEditMode) {
                setIsEditMode(true);
                onToggleAccordion();
              }
            }}
            label="Achieved Date"
            disabled={!isResponsibleSet || isFormBlocked}
            maxDate={new Date()}
            InputLabelProps={{ style: { fontSize: '0.8rem', color: isEditMode && !achievedDate ? '#d32f2f' : '#777' } }}
            sx={isEditMode && !achievedDate ? {
              '& .MuiInput-underline:before': { borderBottomColor: '#d32f2f' },
              '& .MuiInput-underline:after': { borderBottomColor: '#d32f2f' },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#d32f2f' },
              '& .MuiInputLabel-root': { color: '#d32f2f !important' },
            } : {}}
          />
        </Box>

        {/* Edit/Update Buttons */}
        {isResponsibleSet ? (
          <Box className="filBtn float-left" onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined" 
              disabled={!effectiveEnabled || isFormBlocked}
              onClick={() => { setIsEditMode(true); setFormClosed(false); onToggleAccordion(); }}
              sx={{ minWidth: '60px', fontSize: '0.75rem' }}
            >
              Edit
            </Button>
            <Button 
              variant="outlined" 
              disabled={!effectiveEnabled || isFormBlocked || !responsiblePerson || !plannedDate} 
              onClick={handleUpdateResponsiblePerson}
              sx={{ minWidth: '70px', fontSize: '0.75rem' }}
            >
              Update
            </Button>
          </Box>
        ) : (
          <Box className="filBtn float-left" onClick={(e) => e.stopPropagation()}>
            <Button variant="outlined" disabled={!effectiveEnabled || isFormBlocked || !responsiblePerson || !plannedDate} onClick={handleUpdateResponsiblePerson}>Set</Button>
          </Box>
        )}
      </Box>

      {/* Success/Error Messages */}
      {(saveNotice || saveError) && (
        <Box sx={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, minWidth: '300px' }}>
          {saveError && <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>}
          {saveNotice && <Alert severity="success" onClose={() => setSaveNotice(null)}>{saveNotice}</Alert>}
        </Box>
      )}

      {expanded && (isEditMode || readOnly) && (
        <Accordion expanded={expanded} sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box className="accordion-close-button-container">
              <Button
                className="close-button"
                variant="contained"
                color="secondary"
                startIcon={<CloseIcon />}
                onClick={() => { setIsEditMode(false); setFormClosed(true); handleCloseForm(); }}
              >
                Close Form
              </Button>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={isFormBlocked ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
            <Grid container spacing={4} className="d2-container">
              <Grid size={{ xs: 12, md: 6 }} className="d2-left">
                <Box className="question-block">
                  <Box className="question-label">
                    <span className="d2-qnum">Q1</span>
                    <span className="d2-question-text">Occurrence</span>
                  </Box>
                  {renderTable(occurrenceRows, setOccurrenceRows, true)}
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }} className="d2-right">
                <Box className="question-block">
                  <Box className="question-label">
                    <span className="d2-qnum">Q2</span>
                    <span className="d2-question-text">Non Detection</span>
                  </Box>
                  {renderTable(nonDetectionRows, setNonDetectionRows, false)}
                </Box>
              </Grid>
            </Grid>

            <ImageUpload onFilesSelected={(files) => {
              uploadedFiles.forEach(ef => {
                const efId = ef.AttachmentId || ef.attachmentId;
                if (efId && !files.some(f => (f.AttachmentId || f.attachmentId) === efId)) {
                  setAttachmentsToRemove(prev => prev.includes(efId) ? prev : [...prev, efId]);
                }
              });
              setUploadedFiles(files);
              setIsFormTouched(true);
            }} folderPath="D4" existingFiles={uploadedFiles} maxFiles={3} rejectedExtensions={['doc', 'docx']} />

            <Box className="save-btn-container">
              <Button 
                variant="contained" 
                className="save-btn" 
                onClick={handleSave}
                disabled={saving || !isFormTouched || isFormBlocked}
                sx={{
                  backgroundColor: isFormTouched ? '#800040' : '#ccc',
                  color: '#fff',
                  borderRadius: '20px',
                  textTransform: 'none',
                  px: 4,
                  cursor: isFormTouched ? 'pointer' : 'not-allowed'
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>

            {/* Save notices */}
            {saveError && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error">{saveError}</Alert>
              </Box>
            )}
            {saveNotice && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="success">{saveNotice}</Alert>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};

export default D4;
